import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Send from "../../core/communication"
import { setData } from "../../core/redux/app/Actions"
import { Navigate } from "react-router-dom"
import LoginScreen from "../../screen/Login"
import { useForm } from "react-hook-form"
import 'react-toastify/dist/ReactToastify.css';
import { firebaseAuth } from "../../core/firebase"
import { isDashboard, isChatPlatform, isChatOnlyPlatform } from "../../routes"
import { customToast } from "../../components/Toast"
import localforage from "localforage"
import { QueryCache } from '@tanstack/react-query'
import mixpanel from 'mixpanel-browser'; // Import mixpanel
import { getPlatform } from "../../core/utils/platform"
import { useProjects } from "../SelectProjectCompanyContainer"

import Impactools from "../../assets/img/logosImpactools/logoImpactools.svg"
import ImpactoolsMedicao from "../../assets/img/logosImpactools/logoImpactoolsMedicao.svg"
import ImpactoolsConexão from "../../assets/img/logosImpactools/logoImpactoolsConexao.svg"
import ImpactoolsOperacao from "../../assets/img/logosImpactools/logoImpactoolsOperacao.svg"
import ManejeLogoEmbrapa from "../../assets/img/logosManeje/manejebem-embrapa-logo.svg"
import { Helmet } from "react-helmet"
import { platformDetails } from "../../core/utils/platformDetails"

// import { getAuth, signInWithCustomToken } from "firebase/auth";

export const logout = () => {
  mixpanel.track("Logout", {
    timestamp: new Date().toISOString()
  });
  mixpanel.reset();
  firebaseAuth().signOut();
  const queryCache = new QueryCache()
  localforage.clear()
  queryCache.clear()
  window.location.href = "/authentication/login"
}

const LoginContainer = () => {
  const [isRecovery, setIsRecovery] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const token = useSelector((state) => state.app.token)
  const currentUser = useSelector((state) => state.app.user)
  const dispatch = useDispatch()
  
  // Para chat isolado, garante que projetos sejam carregados após autenticação
  // O useProjects() agora gerencia automaticamente o carregamento quando o token existe
  useProjects()

  const url = window.location.href

  const { register, handleSubmit, resetField } = useForm({
    mode: "onChange",
  })

  // Autenticação via token na URL para chat isolado
  useEffect(() => {
    // Ler parâmetros da URL de forma alternativa
    const urlParams = new URLSearchParams(window.location.search)
    const urlToken = urlParams.get('token')
    const authToken = urlParams.get('auth')
    const accessToken = urlToken || authToken
    
    // Debug completo da situação
    console.log('Token Auth Debug:', {
      platform: getPlatform(),
      isChatOnlyPlatform,
      hasAccessToken: !!accessToken,
      hasToken: !!token,
      isAuthenticating,
      currentUrl: window.location.href,
      urlParams: Object.fromEntries(urlParams),
      accessToken: accessToken ? accessToken.substring(0, 20) + '...' : null
    })
    
    // Só permite autenticação via token se for plataforma de chat isolado (4) e usuário não estiver logado
    if ((getPlatform() === "4" || isChatOnlyPlatform) && accessToken && !token && !currentUser?.access_token && !isAuthenticating) {
      console.log('Iniciando autenticação via token...')
      setIsAuthenticating(true)
      
      // Fazer login direto com o token - chamada direta ao servidor
      const serverUrl = process.env.REACT_APP_SERVER || "https://be.manejechat.com"
      console.log('Verificando token no servidor:', serverUrl)
      
      fetch(`${serverUrl}/auth/login-with-external-token`, {
        method: 'POST',
        headers: {
          "Content-type": "application/json"
        },
        body: JSON.stringify({
          token: accessToken
        })
      })
        .then(response => {
          console.log('Response status:', response.status)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((response) => {
          console.log('Login response received:', response)
          
          // login_with_token deve retornar na mesma estrutura que /auth/login
          // ou seja: { user: {...}, access_token: "...", firebase_token: "..." }
          const user = response.user || response
          const responseToken = response.access_token || accessToken
          const roles = user && user.roles && user.roles.role
          
          console.log('Processed user data:', { user, responseToken, roles })
          
          if (user) {
            const allowedUsers = ["USER_COMMUNICATION", "USER_ADMIN", "USER_TEC", "USER_MANAGER"].includes(roles);
            
            if (allowedUsers) {
              dispatch(setData({
                token: responseToken,
                user: {
                  ...response,
                  access_token: responseToken
                },
                currentUserRole: roles,
                isUserAdmin: roles === "USER_ADMIN"
              }))
            } else {
              customToast("Acesso permitido somente para usuários autorizados", "error")
              setIsAuthenticating(false)
              return
            }
            
            // Autenticar no Firebase se tiver token
            const firebaseToken = response.firebase_token || user?.firebase_token
            if (firebaseToken) {
              firebaseAuth().signInWithCustomToken(firebaseToken)
                .then((firebaseResponse) => {
                  console.log('firebase logged via URL token', { firebaseResponse })
                  
                  // Mixpanel tracking
                  if (user.email) {
                    mixpanel.identify(user.email);
                    mixpanel.people.set({
                      "$name": user.name,
                      "$email": user.email,
                      "User ID": user.id,
                      "Role": roles
                    });
                    mixpanel.track("Login via URL Token", {
                      "User ID": user.email,
                      "Role": roles,
                      "Platform": getPlatform()
                    });
                  }
                })
                .catch(e => console.log('firebase login error', { e }))
            }
            
            customToast("Login realizado com sucesso", "success")
            console.log('Login via token realizado com sucesso!')
          } else {
            console.error('Resposta do servidor não contém dados do usuário')
            customToast("Erro na autenticação", "error")
            setIsAuthenticating(false)
          }
        })
        .catch((error) => {
          console.error('Token authentication failed:', error)
          customToast("Token inválido ou expirado", "error")
          setIsAuthenticating(false)
        })
    }
  }, [token])

  useEffect(() => {
    resetField("password")
  }, [isRecovery])

  const handleIsRecovery = () => {
    setIsRecovery(!isRecovery)
  }

  const onLogin = (data) => {
    const { username, password } = data
    try {
      if (!username || !password) {
        customToast('Falta Campos', "info");
      }
      else {
        Send.post("/auth/login", { username, password }, false).then((user) => {
          const roles = user && user.user.roles && user.user.roles.role

          const allowedUsers = ["USER_COMMUNICATION", "USER_ADMIN", "USER_TEC", "USER_MANAGER"].includes(roles);

          if (user && user.firebase_token) {
            firebaseAuth().signInWithCustomToken(user.firebase_token)
              .then((response) => {
                console.log('firebase logged', { response })
                // Add Mixpanel user identification
                mixpanel.identify(user.user.email);
                mixpanel.people.set({
                  "$name": user.user.name,
                  "$email": user.user.email,
                  "User ID": user.user.id,
                  "Role": roles
                });
                mixpanel.track("Login Successful", {
                  "User ID": user.user.email,
                  "Role": roles
                });
              }).catch(e => { console.log('firebase login error', { e }) })
          }

          if (allowedUsers) {
            dispatch(setData({
              token: user.access_token,
              user,
              currentUserRole: roles,
              isUserAdmin: roles === "USER_ADMIN"
            }))
          } else {
            customToast(
              isChatPlatform ?
                'Acesso permitido somente para usuários do tipo Comunicação' :
              !isDashboard ? 
                'Acesso permitido somente para Técnico, Administrador ou Gerente' :
                'Acesso permitido somente para Administrador ou Gerente',
              "warn"
            );
          }
        }).catch((e) => {
          console.log({e});
          customToast(
            `Usuário ou Senha Incorretos`,
            "error"
          );
        })
      }
    } catch (e) {
      customToast(
        'Acesso permitido para usuários técnico, administrador ou gerente',
        "error"
      );
    }
  }

  const onRecovery = async (data) => {
    const { username } = data
    try {
      if (!username) {
        customToast(
          'Preencha o campo email',
          "info"
        );
      } else {
        Send.post("/auth/remember", { info: username }).then((user) => {
          console.log("user", user)
          if(user && user.status === 'ERROR') {
            throw new Error(user?.message)
          }
          customToast(
            "Você receberá um link de recuperação no seu email",
            "success"
          );
        }).catch((e) => {
          console.log("E", e)
          customToast(
            `Erro ao recuperar senha, verifique o email digitado`,
            "error"
          );
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  const { platformName, platformLogo } = platformDetails()


  if (token || (currentUser && currentUser.access_token)) {
    return <Navigate to={{ pathname: "/" }} />
  }

  const handleCantAccess = () => {
    const message = `Olá, preciso de ajuda com o login.`;
    window.open(encodeURI(`https://wa.me/559892487263?text=${message}`), "_blank", 'noopener,noreferrer')
  }
  
  // Mostrar loading enquanto autentica via token
  if (isAuthenticating && (getPlatform() === "4" || isChatOnlyPlatform)) {
    return (
      <>
        <Helmet>
          <title>{`Autenticando... | ${platformName}`}</title>
        </Helmet>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f5f5f5'
        }}>
          <img src={platformLogo} alt="Logo" style={{ width: '200px', marginBottom: '20px' }} />
          <h2>Autenticando...</h2>
          <p>Por favor, aguarde enquanto validamos seu acesso.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Login | ${platformName}`}</title>
      </Helmet>
      <LoginScreen
        handleSubmit={!isRecovery ? handleSubmit(onLogin) : handleSubmit(onRecovery)}
        handleIsRecovery={handleIsRecovery}
        isRecovery={isRecovery}
        register={register}
        logo={platformLogo}
        handleCantAccess={handleCantAccess}
      />
    </>
  )
}

export default LoginContainer
